// In-app intake entry point (docs/app.md → Upload). Drag-and-drop a PDF to
// start a new invoice. The send (in `lib/uploadTracker`, since D-205) is one
// call per file: `uploadDocument` asks the API
// where to put the file, PUTs the bytes straight to storage, then registers the
// upload under an idempotency key so a retry cannot create a second row or a
// second extraction charge. The extraction pipeline takes over from there.
//
// The old flow took the signed-in user's id, email and name as arguments and
// reported success through a `flow_state` field; both are gone — the API knows
// who is calling, and a failure throws.
//
// PROGRESS IS NOT SHOWN HERE ANY MORE (owner, 2026-09-03). Each sent file used
// to be polled and reported on its own row, under an "N of M processed" line.
// The trouble is where that lived: an uploader presses Send and closes this
// window, and the tracking died with it. Sending now hands the upload ids to
// `lib/uploadTracker`, and `UploadProgressToast` — mounted in the app shell,
// not in this dialog — watches them through the pipeline and fades away thirty
// seconds after the last one finishes. This screen's job ends at "sent".
//
// THE POP-UP CLOSES ON SUBMIT (owner, 2026-09-29, D-205): "please change the
// blue Upload button to say, "Submit", instead. Also, the pop-up should close
// automatically after this button is clicked." So the send no longer runs
// here. Submit hands the staged files and the one Company to
// `sendUploads` (`lib/uploadTracker`), which runs at module level and lists
// every file in the progress toast straight away — Uploading…, then the
// server's stages, or Not sent with the API's reason. The toast is where a
// failed send is reported now, because this window is gone by then.
//
// The success toast that used to fire here went with it. It said the documents
// had been received and pointed at this window for progress; the progress toast
// now says the same thing and then keeps saying it, so a second toast beside it
// was two notifications for one event.
//
// Open to any signed-in user: every uploaded document goes through the whole
// pipeline, so restricting who may start one buys nothing.
//
// THE COMPANY PICKER (owner item 21, 2026-08-31): "I want to offer an uploader
// an opportunity to pre-determine the Company value for the attached uploads
// with a dropdown or something. Stick with one dropdown selector, even if
// multiple files are attached, and apply the chosen Company value to all
// attached files." So there is ONE picker for the whole window, never one per
// row, and `handleSend` passes the same id with every file.
//
// Optional, always: leaving it empty is the old behaviour exactly, and the
// matching ladder falls through to the uploader's mail domain, an authored
// company alias and the model, in that order.
//
// The picker OFFERS only companies this uploader may actually file against,
// which is a courtesy and not the control: `POST /uploads/register` validates
// the id against the caller's own company scope server-side and answers 403
// otherwise (`docs/security.md` §3.6). An uploader with no company scope at
// all sees no picker, because there is nothing they could legally choose.
import { useEffect, useRef, useState } from 'react'
import {
  Button,
  Body1,
  Caption1,
  Field,
  MessageBar,
  MessageBarBody,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import {
  ArrowUploadRegular,
  DocumentRegular,
  DeleteRegular,
  DismissRegular,
} from '@fluentui/react-icons'
import { searchCompanies } from '../lib/data'
import { loadSession } from '../lib/identity'
import { sendUploads } from '../lib/uploadTracker'
import { EntityPicker, type EntityOption } from '../components/EntityPicker'

const useStyles = makeStyles({
  // Body1 renders a span; block display so the bottom margin actually spaces.
  sub: { display: 'block', color: tokens.colorNeutralForeground3, marginBottom: '16px' },
  /** The one company selector for the whole window (owner item 21). The clear
      button sits beside the field rather than inside it — `EntityPicker` has
      no clear affordance of its own, and adding one to every picker in the app
      for this one caller would be the wrong place to change. It is an X ON THE
      LINE OF THE COMBOBOX since 2026-09-03 (owner); it was the word "Clear",
      which read as a second control rather than as part of the field. */
  companyPicker: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '4px',
    marginBottom: '16px',
    '& > *:first-child': { flex: 1, minWidth: 0 },
  },
  dropzone: {
    border: `2px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    padding: '48px 24px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  dropzoneActive: {
    border: `2px dashed ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  // A STANDARD HEIGHT, AND THE LIST SCROLLS (owner, 2026-09-28): twenty files
  // stretched the dialog to the full window. About five rows show at once; the
  // picker, drop zone and Submit button stay in view.
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    margin: '16px 0',
    maxHeight: '300px',
    overflowY: 'auto',
    paddingRight: '4px',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  itemMain: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' },
  itemName: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  itemNote: { color: tokens.colorNeutralForeground3 },
  // Breathing room below the drop zone (or staged-file list) before the actions.
  bar: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px' },
})

// PDF only: the API refuses anything else with `unsupported_media_type`, so the
// picker should not offer a file that will be rejected.
const ACCEPT = '.pdf'
const MAX_BYTES = 32 * 1024 * 1024

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

interface UploadScreenProps {
  /** False once the host dialog closes. The company scope is read on each open;
      nothing else here depends on it, because the progress tracking that used
      to stop with this flag now lives in the app shell and deliberately does
      NOT stop when the dialog closes. */
  open?: boolean
  /** Called the moment Submit is pressed; the host closes the dialog (D-205).
      The send carries on without it. */
  onSubmitted?: () => void
}

export function UploadScreen({ open = true, onSubmitted }: UploadScreenProps) {
  const s = useStyles()
  /** The staged files. Only staged: once Submit is pressed they belong to the
      progress toast, and this list empties. */
  const [items, setItems] = useState<File[]>([])
  const [dragging, setDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /**
   * ONE company for the whole window (owner item 21). Held here rather than
   * per `Item`, because the owner asked for one selector however many files
   * are attached — the shape of the state is the shape of the rule.
   */
  const [company, setCompany] = useState<{ id?: string; name?: string }>({})
  /**
   * Which companies this uploader may choose. `null` while loading, and the
   * picker is not rendered until it resolves — an empty `ids` for a
   * non-internal caller means NO company may be chosen, so no picker is
   * offered rather than one that would 403 on every option.
   */
  const [scope, setScope] = useState<{ internal: boolean; ids: readonly string[] } | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // The caller's own scope, read once per open. A failure leaves `scope` null
  // and simply hides the picker — an intake screen must never be blocked by an
  // optional convenience.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    void loadSession()
      .then((session) => {
        if (cancelled) return
        setScope({ internal: session.isInternal, ids: session.visibleCompanyIds })
      })
      .catch(() => {
        if (!cancelled) setScope(null)
      })
    return () => {
      cancelled = true
    }
  }, [open])

  const canPickCompany = scope !== null && (scope.internal || scope.ids.length > 0)

  function addFiles(list: FileList | null) {
    if (!list) return
    const incoming = Array.from(list)
    const tooBig = incoming.filter((f) => f.size > MAX_BYTES)
    const ok = incoming.filter((f) => f.size <= MAX_BYTES)
    if (tooBig.length) {
      setError(
        `${tooBig.length === 1 ? `“${tooBig[0].name}” is` : `${tooBig.length} files are`} over the 32 MB limit and can’t be uploaded here. Email larger documents to the intake inbox instead.`,
      )
    } else if (ok.length) {
      setError(null)
    }
    if (ok.length) setItems((prev) => [...prev, ...ok])
  }

  // SUBMIT HANDS OFF AND CLOSES (D-205). The files and the one Company are
  // read here, once, and passed to `sendUploads`, which lists every file in
  // the toast before its first byte goes up. It is not awaited: this window
  // closes now, and everything after — the upload, a failure and its reason,
  // the stages — belongs to the toast, which outlives it.
  //
  // THE WINDOW STILL RESETS ITSELF (owner, 2026-09-03): the list and the
  // Company clear, so reopening it starts a fresh batch. Leaving the Company
  // set would quietly file the NEXT batch against a company chosen for this
  // one, which is the kind of mistake nobody notices until Import Control.
  function handleSubmit() {
    if (items.length === 0) return
    // THE SAME id for every file in the batch, by construction — there is one
    // picker and one piece of state behind it.
    void sendUploads(items, company.id ?? null)
    setItems([])
    setCompany({})
    setError(null)
    onSubmitted?.()
  }

  const pendingCount = items.length

  return (
    <div>
      <Body1 className={s.sub}>Drop invoices here to start the approval process.</Body1>

      {error && (
        <MessageBar intent="error" style={{ marginBottom: 16 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {canPickCompany && (
        <div className={s.companyPicker}>
          {/* No hint line since 2026-09-03 (owner). The placeholder says what
              the field is for, in the field, which is where someone about to
              type is looking; the hint repeated it underneath and pushed the
              drop zone down the dialog. "(optional)" left the label with
              D-236: a label is a title or a sentence. With no required
              marker beside it, the title still reads as optional. */}
          <Field label="Company">
            <EntityPicker
              value={company}
              placeholder="Select the company responsible for approving all attached files."
              search={(t) =>
                searchCompanies(t).then<EntityOption[]>((companies) =>
                  companies
                    .filter((c) => scope.internal || scope.ids.includes(c.company_id))
                    .map((c) => ({ id: c.company_id, primary: c.company_name })),
                )
              }
              onSelect={(o) => setCompany({ id: o.id, name: o.primary })}
              // Emptied, the Company clears (D-295); the Clear button below is its button.
              onClear={() => setCompany({})}
              clearButton={false}
            />
          </Field>
          {company.id && (
            <Button
              appearance="subtle"
              icon={<DismissRegular />}
              aria-label="Clear Company"
              title="Clear"
              onClick={() => setCompany({})}
            />
          )}
        </div>
      )}

      <div
        className={mergeClasses(s.dropzone, dragging && s.dropzoneActive)}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          addFiles(e.dataTransfer.files)
        }}
      >
        <ArrowUploadRegular fontSize={40} />
        <Body1>Drag files here, or click to browse</Body1>
        <Caption1>PDF, up to 32 MB each</Caption1>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          hidden
          onChange={(e) => addFiles(e.target.files)}
        />
      </div>

      {items.length > 0 && (
        <div className={s.list}>
          {items.map((file, i) => (
            <div key={`${file.name}-${i}`} className={s.item}>
              <DocumentRegular />
              <span className={s.itemMain}>
                <span className={s.itemName}>{file.name}</span>
                <Caption1 className={s.itemNote}>Ready to send</Caption1>
              </span>
              <Caption1>{formatSize(file.size)}</Caption1>
              <Button
                appearance="subtle"
                size="small"
                icon={<DeleteRegular />}
                aria-label="Remove"
                onClick={() => setItems((prev) => prev.filter((_, j) => j !== i))}
              />
            </div>
          ))}
        </div>
      )}

      <div className={s.bar}>
        <Button
          appearance="primary"
          disabled={pendingCount === 0}
          onClick={handleSubmit}
        >
          {/* "Submit" since 2026-09-29 (owner, D-205), with no file count
              since D-233: "Just Submit is fine." It was "Upload" from
              2026-09-03, and "Send to intake" before that. No icon since
              2026-10-14 (owner). */}
          Submit
        </Button>
        {items.length > 0 && (
          <Button appearance="subtle" onClick={() => setItems([])}>Clear</Button>
        )}
      </div>
    </div>
  )
}
